import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/utils/api-request';

export interface WorkflowFileInput {
  node_instance_id: string;
  node_display_name: string;
  field_id: string;
  display_name: string;
}

interface InputField extends WorkflowFileInput {
  valid_types: string[];
}

/**
 * The file-input parameters a trigger can deliver a file to.
 *
 * Reads the same endpoint the run path uses, so the options here are exactly
 * the ones the backend will accept as an `input_binding` — a node that is not
 * listed would be rejected at subscription create.
 */
export const useWorkflowFileInputs = (
  orgId?: string,
  workflowId?: string,
  enabled = true,
) =>
  useQuery<WorkflowFileInput[]>({
    queryKey: ['workflow-file-inputs', orgId, workflowId],
    enabled: !!orgId && !!workflowId && enabled,
    // This describes the shape of a workflow that is being edited on the very
    // screen this runs from, and nothing invalidates the key when a node is
    // added. The app-wide 5-minute staleTime would serve the pre-edit graph and
    // silently offer the wrong binding, so always refetch.
    staleTime: 0,
    queryFn: async () => {
      const response = await apiRequest<{ inputs?: InputField[] }>({
        url: `/api/v1/orgs/${orgId}/workflows/${workflowId}/inputs`,
        method: 'GET',
        target: 'workflow',
      });
      if (response?.detail) {
        throw new Error(response.detail || 'Could not load workflow inputs');
      }
      return (response?.data?.inputs ?? [])
        .filter((i) => i.valid_types?.includes('file'))
        .map(
          ({
            node_instance_id,
            node_display_name,
            field_id,
            display_name,
          }) => ({
            node_instance_id,
            node_display_name,
            field_id,
            display_name,
          }),
        );
    },
  });
